/**
 * 2026-10-16 (owner) · Issue Batch is submitted only after the batch's BOM is loaded.
 *
 * The form shows "Load BOM" in place of Submit — always. It opens a modal that first asks HOW:
 *   · Load from SAP  — each material's AR(s) with their assay, as SAP answers (a dummy for now)
 *   · Fill manually  — the BOM listed, and for each material its AR No. and assay typed in (a second AR
 *                      each with the quantity taken from it and its assay)
 * Either way the quantity to take from each AR is worked out (BOM × 100 ÷ assay; split over ARs each by
 * its own assay — services/batch_bom/sap_bom_import.py), shown in the same table, and the batch is issued
 * from the modal's own Submit. The server refuses an issue without a loaded BOM (issue_check).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Space, Table, Tag, Tooltip } from 'antd';
import { CloudDownload, PencilLine, Plus, Trash2 } from 'lucide-react';
import { get_bill_of_materials } from '@/utils/api';

const tokenHeader = () => {
  const t = localStorage.getItem('Token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
// quantities shown rounded to 3 decimals (a round figure, not 0.247250)
const r3 = (v) => Math.round((Number(v) + Number.EPSILON) * 1000) / 1000;
const fmt = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(3));   // always 3 decimals
const COUNTED = ['no', 'nos', 'nos.', 'pcs', 'pc', 'units', 'unit', 'ea', 'each', 'numbers', 'vials', 'pieces', 'm'];
const isCounted = (r) => COUNTED.includes(String(r?.unit || '').trim().toLowerCase());
const post = async (url, body) => {
  try {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', ...tokenHeader() },
                                   body: JSON.stringify(body) });
    return await res.json().catch(() => ({}));
  } catch {
    return { status: 'failed', message: 'The server did not answer. Try again.' };
  }
};

/** The formula, stated once at the top — how every quantity below is worked out. */
function FormulaBox() {
  const line = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13.5, lineHeight: 1.7 };
  return (
    <div style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
      <div style={line}><b>Pure from AR = Qty taken × Assay % ÷ 100</b></div>
      <div style={{ fontSize: 13, color: '#4b5563' }}>The pure from all of a material's ARs together matches its BOM quantity. Counted items (Nos): the quantities together match the BOM quantity.</div>
    </div>
  );
}

export default function SapBomGate({ batchNumber, recipeId, batchSize, children, submitLabel = 'Submit', onSubmit, icon,
  validate, values }) {
  const [open, setOpen] = useState(false);
  // choose → (sap | manual-edit) → result
  const [stage, setStage] = useState('choose');
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState({});       // manual: {sap_code: [{ar_number, quantity, assay}]}

  // enabled only once the form passes the same validation Submit uses
  const [formOk, setFormOk] = useState(!validate);
  // The latest validate, held aside: it is a new function every render, and re-running on its
  // identity (validating updates the form, which re-renders) would never stop. Values decide.
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const valuesKey = useMemo(() => { try { return JSON.stringify(values ?? null); } catch { return String(Date.now()); } }, [values]);
  useEffect(() => {
    if (!validateRef.current) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      Promise.resolve(validateRef.current({ touch: false }))
        .then((errs) => alive && setFormOk(!errs || !Object.keys(errs).length));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [valuesKey]);

  const loadTable = useCallback(() => new Promise((resolve) => {
    get_bill_of_materials({ recipe_id: recipeId, batch_size: batchSize, batch_number: batchNumber }, (res) => {
      const data = res?.data?.data || [];
      setRows(data);
      resolve(data);
    });
  }), [recipeId, batchSize, batchNumber]);

  const arsOf = (r) => ((r.ars && r.ars.length) ? r.ars
    : (r.ar_number ? [{ sequence: 1, ar_number: r.ar_number, assay_percent: r.assay_percent,
                        quantity_to_take: r.quantity_to_take, pure_quantity: r.pure_quantity }] : []));

  // "Load BOM": the form is checked first, exactly as Submit would check it
  const openLoad = async () => {
    if (validate) {
      const errs = await validate({ touch: true });
      if (errs && Object.keys(errs).length) { setFormOk(false); return; }
    }
    setError(''); setStage('choose'); setOpen(true);
    if (!recipeId) setError('Choose the MBMR first.');
    else if (!batchNumber) setError('The batch number is not assigned yet.');
  };

  const loadFromSap = async () => {
    setStage('result'); setSource('SAP'); setLoading(true); setError(''); setRows([]);
    const body = await post('/bp/sap/bom-import/', { batch_number: batchNumber, recipe_id: recipeId, batch_size: batchSize });
    if (body?.status !== 'success') setError(body?.message || 'The BOM could not be loaded from SAP.');
    else await loadTable();
    setLoading(false);
  };

  const startManual = async () => {
    setStage('manual'); setSource('MANUAL'); setLoading(true); setError('');
    const data = await loadTable();
    // what is already on the batch fills the boxes in; otherwise one empty AR per material
    const next = {};
    data.forEach((r) => {
      const have = arsOf(r);
      next[r.sap_code] = have.length
        ? have.map((a) => ({ ar_number: a.ar_number || '', quantity: a.quantity_to_take ?? null, assay: a.assay_percent ?? null }))
        : [{ ar_number: '', quantity: null, assay: null }];
    });
    setEntries(next);
    setLoading(false);
  };

  // Pure = Qty taken × Assay % ÷ 100, per AR, as it is typed — and whether the total matches the BOM.
  const liveCalc = (r) => {
    const list = entries[r.sap_code] || [];
    const counted = isCounted(r);
    const per = list.map((a) => {
      const q = Number(a.quantity);
      if (!(a.quantity !== null && a.quantity !== '' && Number.isFinite(q))) return null;
      if (counted) return r3(q);
      const as = Number(a.assay);
      return as > 0 && as <= 100 ? r3((r3(q) * as) / 100) : null;
    });
    const complete = list.length > 0 && per.every((v) => v != null);
    const total = complete ? per.reduce((t, v) => t + v, 0) : null;
    const bom = Number(r.batch_quantity) || 0;
    // rounding each AR to 3 decimals may move its pure by half a gram-place — allowed, as the server does
    const match = complete && Math.abs(total - bom) <= Math.max(bom * 0.001, 0.0005 * list.length) + 1e-9;
    return { per, total, bom, match, complete, counted };
  };

  const setAr = (sap, i, patch) => setEntries((e) => ({ ...e, [sap]: e[sap].map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  const addAr = (sap) => setEntries((e) => ({ ...e, [sap]: [...(e[sap] || []), { ar_number: '', quantity: null, assay: null }] }));
  const removeAr = (sap, i) => setEntries((e) => ({ ...e, [sap]: e[sap].filter((_, j) => j !== i) }));

  const saveManual = async () => {
    setLoading(true); setError('');
    const body = await post('/bp/sap/bom-import/manual/', { batch_number: batchNumber, recipe_id: recipeId,
                                                           batch_size: batchSize, entries });
    if (body?.status !== 'success') { setError(body?.message || 'The BOM could not be saved.'); setLoading(false); return; }
    await loadTable();
    setStage('result');
    setLoading(false);
  };

  const submitFromModal = () => { setOpen(false); onSubmit?.(); };

  // ── the result table: the BOM, every row opening onto its AR(s) ─────────────────────────────────
  const columns = [
    { title: 'Material code', dataIndex: 'material_code', key: 'material_code', width: 150,
      render: (v, r) => v || r.sap_code || '—' },
    { title: 'Material', dataIndex: 'material_name', key: 'material_name' },
    { title: 'BOM quantity', key: 'bq', align: 'right', width: 150, render: (_, r) => `${fmt(r.batch_quantity)} ${r.unit || ''}` },
    { title: 'Type', dataIndex: 'material_type', key: 'material_type', width: 140 },
    { title: 'AR No.', key: 'ars', width: 330,
      render: (_, r) => {
        const ars = arsOf(r);
        if (!ars.length) return <Tag color="red">not issued</Tag>;
        return (
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {ars.map((a) => <Tag key={a.ar_number} color={ars.length > 1 ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>{a.ar_number}</Tag>)}
          </span>
        );
      } },
  ];
  const arColumns = (r) => [
    { title: 'AR No.', dataIndex: 'ar_number', key: 'ar_number',
      render: (v, a) => <span>{v}{arsOf(r).length > 1 ? <span style={{ color: '#6b7280' }}> · AR {a.sequence} of {arsOf(r).length}</span> : null}</span> },
    { title: 'Assay %', dataIndex: 'assay_percent', key: 'assay_percent', align: 'right', width: 110, render: (v) => (v != null ? String(+Number(v).toFixed(2)) : '—') },
    { title: 'Quantity as per SAP', dataIndex: 'quantity_to_take', key: 'quantity_to_take', align: 'right', width: 180,
      render: (v) => (v == null ? '—' : <b>{fmt(v)} {r.unit || ''}</b>) },
  ];
  const allHaveAr = rows.length > 0 && rows.every((r) => r.ar_number);
  const data = rows.map((r) => ({ ...r, key: `m-${r.sap_code || r.material_name}` }));

  // ── manual entry: the BOM, and each material's AR(s) typed in ───────────────────────────────────
  const manualColumns = [
    { title: 'Material code', dataIndex: 'material_code', key: 'material_code', width: 130, render: (v, r) => v || r.sap_code || '—' },
    { title: 'Material', dataIndex: 'material_name', key: 'material_name' },
    { title: 'BOM quantity', key: 'bq', align: 'right', width: 130, render: (_, r) => `${fmt(r.batch_quantity)} ${r.unit || ''}` },
    {
      title: 'AR No. · Quantity taken · Assay %', key: 'entry', width: 560,
      render: (_, r) => {
        const list = entries[r.sap_code] || [];
        const counted = isCounted(r);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {list.map((a, i) => (
              <Space key={i} size={6} wrap>
                <Input size="small" style={{ width: 170 }} placeholder={`AR No.${list.length > 1 ? ` ${i + 1}` : ''}`}
                       value={a.ar_number} onChange={(e) => setAr(r.sap_code, i, { ar_number: e.target.value })} />
                <InputNumber size="small" style={{ width: 160 }} min={0} precision={counted ? 0 : 3} placeholder="Quantity" addonAfter={r.unit || ''}
                             value={a.quantity} onChange={(v) => setAr(r.sap_code, i, { quantity: v })} />
                {counted ? <Tag>counted — no assay</Tag> : (
                  <InputNumber size="small" style={{ width: 110 }} min={0} max={100} step={0.01} precision={2} placeholder="Assay %"
                               value={a.assay} onChange={(v) => setAr(r.sap_code, i, { assay: v })} />
                )}
                {list.length > 1 ? (
                  <Tooltip title="Remove this AR">
                    <Button size="small" danger icon={<Trash2 className="w-4 h-4" />} onClick={() => removeAr(r.sap_code, i)} />
                  </Tooltip>
                ) : null}
              </Space>
            ))}
            <div>
              <Button size="small" type="dashed" icon={<Plus className="w-4 h-4" />} onClick={() => addAr(r.sap_code)}>
                Add another AR
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      // the pure each AR delivers, worked out as it is typed, and the total against the BOM quantity
      title: 'Calculated pure', key: 'calc', width: 230,
      render: (_, r) => {
        const c = liveCalc(r);
        const u = r.unit || '';
        if (!c.complete) return <span style={{ color: '#9ca3af' }}>Enter the quantity{c.counted ? '' : ' and assay'}</span>;
        return (
          <div style={{ lineHeight: 1.5 }}>
            {c.per.length > 1 ? c.per.map((v, i) => (
              <div key={i} style={{ fontSize: 13, color: '#4b5563' }}>AR {i + 1}: {fmt(v)} {u}</div>
            )) : null}
            <div><b>{fmt(c.total)} {u}</b>{c.per.length > 1 ? <span style={{ color: '#6b7280' }}> total</span> : null}</div>
            {c.match
              ? <Tag color="green" style={{ marginTop: 2 }}>matches BOM</Tag>
              : <Tag color="red" style={{ marginTop: 2 }}>BOM is {fmt(c.bom)} {u}</Tag>}
          </div>
        );
      },
    },
  ];

  const footer = stage === 'choose' ? [
    <Button key="cancel" onClick={() => setOpen(false)}>Cancel</Button>,
  ] : stage === 'manual' ? [
    <Button key="back" onClick={() => { setStage('choose'); setError(''); }} disabled={loading}>Back</Button>,
    <Button key="save" type="primary" loading={loading} onClick={saveManual}>Calculate &amp; save</Button>,
  ] : [
    <Button key="back" onClick={() => { setStage('choose'); setError(''); }} disabled={loading}>Back</Button>,
    source === 'MANUAL'
      ? <Button key="edit" onClick={startManual} disabled={loading}>Edit entries</Button>
      : <Button key="again" onClick={loadFromSap} disabled={loading}>Load again</Button>,
    <Button key="submit" type="primary" icon={icon} disabled={loading || !allHaveAr} onClick={submitFromModal}>
      {submitLabel}
    </Button>,
  ];

  return (
    <>
      {/* 2026-10-16 (owner) · never a plain Submit on the form: the batch is always issued from this modal */}
      <Tooltip title={formOk ? '' : 'Fill the form first — the BOM is loaded for the batch it describes'}>
        <Button type="primary" icon={<CloudDownload size={16} />} onClick={openLoad} disabled={!formOk}>Load BOM</Button>
      </Tooltip>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        width={stage === 'choose' ? 620 : stage === 'manual' ? 1350 : 1150}
        zIndex={2000}
        title={`Load BOM${batchNumber ? ` — ${batchNumber}` : ''}`}
        footer={footer}
      >
        {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

        {stage === 'choose' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { k: 'sap', icon: <CloudDownload size={26} />, t: 'Load from SAP', d: "Each material's AR(s) and assay, as SAP holds them.", go: loadFromSap },
              { k: 'man', icon: <PencilLine size={26} />, t: 'Fill manually', d: 'Type each material\u2019s AR No., the quantity taken from it and its assay.', go: startManual },
            ].map((o) => (
              <button key={o.k} type="button" onClick={o.go} disabled={!recipeId || !batchNumber}
                      style={{ textAlign: 'left', border: '1px solid #d1d5db', borderRadius: 10, padding: 16, background: '#fff',
                               cursor: recipeId && batchNumber ? 'pointer' : 'not-allowed' }}>
                <div style={{ color: '#204448' }}>{o.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 15, marginTop: 6 }}>{o.t}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{o.d}</div>
              </button>
            ))}
          </div>
        ) : null}

        {stage === 'manual' ? (
          <>
            <FormulaBox />
            <div style={{ fontSize: 13.5, color: '#4b5563', marginBottom: 8 }}>
              Enter each material's AR No., the quantity taken from it and its assay. Use "Add another AR" when it comes from more than one AR.
            </div>
            <Table size="small" bordered rowKey={(r) => r.sap_code || r.material_name} loading={loading}
                   dataSource={rows} columns={manualColumns} pagination={false} scroll={{ y: 460 }} />
          </>
        ) : null}

        {stage === 'result' ? (
          <>
            {!loading && rows.length && !error ? (
              <>
                <div style={{ fontSize: 13.5, color: '#4b5563', marginBottom: 8 }}>
                  {source === 'MANUAL' ? 'Filled manually.' : 'Loaded from SAP.'} Expand ▸ on a material for each AR's assay and quantity as per SAP.
                </div>
              </>
            ) : null}
            <Table size="small" bordered rowKey="key" loading={loading}
                   dataSource={data} columns={columns}
                   expandable={{
                     rowExpandable: (r) => arsOf(r).length > 0,
                     expandedRowRender: (r) => (
                       <Table size="small" bordered pagination={false} rowKey={(a) => a.ar_number}
                              dataSource={arsOf(r)} columns={arColumns(r)} />
                     ),
                   }}
                   pagination={{ pageSize: 8, hideOnSinglePage: true }}
                   locale={{ emptyText: loading ? 'Loading the BOM…' : 'Nothing loaded yet' }} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
